import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import {
  CONTACT_LENS_BASE_CURVE_MM,
  CONTACT_LENS_DIAMETER_MM,
  CONTACT_LENS_MODALITIES,
  CYLINDER_RANGE,
  SPHERE_RANGE,
  dioptre,
  isWithin,
  millimetreProblem,
  validateAdd,
  validateAxis,
} from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { ContactLensEyeView, ContactLensPrescriptionView } from './ContactLensSection.js'

/**
 * Issuing a draft contact lens prescription and correcting an active one. `RX-002`;
 * `decisions/0024`, which governs this the way it governs `RxActions`.
 *
 * Both are writes, so both go through the queue (`OFF-001a`). A refusal from a queued write blocks
 * the queue head and everything behind it, so the form refuses locally what the server would
 * refuse and the shopkeeper can act on. The server still checks all of it (a queue posts JSON).
 *
 * A correction replaces the whole eye, so the fields this form does not show (material, wearing
 * schedule, the brand-substitution policy) are carried over from the version being corrected rather
 * than dropped: a typo in a power must not quietly unset a prescriber's brand-binding.
 */

export interface ClRxActionsProps {
  readonly rx: ContactLensPrescriptionView
  /** Called once the server has taken the write, so the parent can read the customer again. */
  readonly onSettled: () => void
}

interface EyeForm {
  power: string
  cylinder: string
  axis: string
  add: string
  baseCurveMm: string
  diameterMm: string
  brand: string
  modality: string
}

const formFrom = (eye: ContactLensEyeView): EyeForm => ({
  power: String(eye.power),
  cylinder: eye.cylinder === undefined ? '' : String(eye.cylinder),
  axis: eye.axis === undefined ? '' : String(eye.axis),
  add: eye.add === undefined ? '' : String(eye.add),
  baseCurveMm: String(eye.baseCurveMm),
  diameterMm: String(eye.diameterMm),
  brand: eye.brand,
  modality: eye.modality,
})

/** An empty box is "not entered"; anything else is a number, or NaN, which is never sendable. */
const num = (text: string): number | undefined => (text.trim() === '' ? undefined : Number(text))

type Problem =
  'reason' | 'required' | 'power' | 'cylinder' | 'add' | 'mm' | { readonly message: string }

function firstProblem(od: EyeForm, os: EyeForm, reason: string): Problem | null {
  if (reason.trim() === '') return 'reason'
  for (const form of [od, os]) {
    const power = num(form.power)
    const baseCurve = num(form.baseCurveMm)
    const diameter = num(form.diameterMm)
    if (
      power === undefined ||
      baseCurve === undefined ||
      diameter === undefined ||
      form.brand.trim() === '' ||
      form.modality === ''
    ) {
      return 'required'
    }
    try {
      if (!isWithin(dioptre(power), SPHERE_RANGE)) return 'power'
    } catch {
      return 'power'
    }
    const cylinder = num(form.cylinder)
    try {
      if (cylinder !== undefined && !isWithin(dioptre(cylinder), CYLINDER_RANGE)) return 'cylinder'
    } catch {
      return 'cylinder'
    }
    if (
      millimetreProblem(baseCurve, CONTACT_LENS_BASE_CURVE_MM) !== null ||
      millimetreProblem(diameter, CONTACT_LENS_DIAMETER_MM) !== null
    ) {
      return 'mm'
    }
    const axis = num(form.axis)
    const blocking = validateAxis(axis, dioptre(cylinder ?? 0)).find((f) => f.severity === 'block')
    if (blocking) return { message: blocking.message }
    const add = num(form.add)
    if (add !== undefined) {
      try {
        const addBlock = validateAdd(dioptre(add)).find((f) => f.severity === 'block')
        if (addBlock) return { message: addBlock.message }
      } catch {
        return 'add'
      }
    }
  }
  return null
}

const MODALITY_KEY = {
  daily: 'clRx.modality.daily',
  two_weekly: 'clRx.modality.twoWeekly',
  monthly: 'clRx.modality.monthly',
  quarterly: 'clRx.modality.quarterly',
  annual: 'clRx.modality.annual',
} as const

export function ClRxActions({ rx, onSettled }: ClRxActionsProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [open, setOpen] = useState(false)
  const [forms, setForms] = useState<{ od: EyeForm; os: EyeForm }>(() => ({
    od: formFrom(rx.od),
    os: formFrom(rx.os),
  }))
  const [reason, setReason] = useState('')
  const [writeId, setWriteId] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, () => {
    setOpen(false)
    setReason('')
    onSettled()
  })

  const issue = useCallback(async () => {
    const write = (await enqueue({
      method: 'POST',
      path: `/contact-lens-prescriptions/${rx.id}/activate`,
      label: t('rxAction.issue'),
      body: {},
    })) as { id: string }
    setWriteId(write.id)
  }, [enqueue, rx.id, t])

  const problem = firstProblem(forms.od, forms.os, reason)

  const eyeBody = (side: 'od' | 'os'): Record<string, unknown> => {
    const f = forms[side]
    const kept = rx[side]
    const cylinder = num(f.cylinder)
    const axis = num(f.axis)
    const add = num(f.add)
    return {
      power: num(f.power),
      ...(cylinder === undefined ? {} : { cylinder }),
      ...(axis === undefined ? {} : { axis }),
      ...(add === undefined ? {} : { add }),
      baseCurveMm: num(f.baseCurveMm),
      diameterMm: num(f.diameterMm),
      brand: f.brand.trim(),
      modality: f.modality,
      ...(kept.material === undefined ? {} : { material: kept.material }),
      ...(kept.wearingSchedule === undefined ? {} : { wearingSchedule: kept.wearingSchedule }),
      ...(kept.brandSubstitutionAllowed === undefined
        ? {}
        : { brandSubstitutionAllowed: kept.brandSubstitutionAllowed }),
    }
  }

  const correct = useCallback(async () => {
    const write = (await enqueue({
      method: 'POST',
      path: `/contact-lens-prescriptions/${rx.id}/versions`,
      label: t('rxAction.correctLabel', { version: rx.version }),
      body: { od: eyeBody('od'), os: eyeBody('os'), reason: reason.trim() },
    })) as { id: string }
    setWriteId(write.id)
  }, [enqueue, forms, reason, rx.id, rx.version, t])

  if (rx.status === 'draft') {
    return (
      <>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            void issue()
          }}
        >
          {t('rxAction.issue')}
        </button>
        <p className="lm-hint">{t('rxAction.issueHint')}</p>
        <p className="lm-queue-state" role="status">
          {statusLine(t, snapshot, writeId, t('queue.subject.rxIssue'))}
        </p>
      </>
    )
  }

  if (rx.status !== 'active') return null

  if (!open) {
    return (
      <button
        type="button"
        className="lm-button lm-button--secondary"
        onClick={() => {
          setForms({ od: formFrom(rx.od), os: formFrom(rx.os) })
          setOpen(true)
        }}
      >
        {t('rxAction.correct')}
      </button>
    )
  }

  const setField = (side: 'od' | 'os', field: keyof EyeForm, value: string): void => {
    setForms((prev) => ({ ...prev, [side]: { ...prev[side], [field]: value } }))
  }

  const text = (side: 'od' | 'os', field: keyof EyeForm, label: string): JSX.Element => (
    <label className="lm-field">
      <span className="lm-field__label">{label}</span>
      <input
        className="lm-input"
        type="text"
        inputMode={field === 'brand' ? 'text' : 'decimal'}
        dir={field === 'brand' ? 'auto' : 'ltr'}
        value={forms[side][field]}
        onChange={(e) => {
          setField(side, field, e.currentTarget.value)
        }}
      />
    </label>
  )

  const message =
    problem === null || problem === 'reason'
      ? null
      : problem === 'required'
        ? t('clRxAction.fieldsRequired')
        : problem === 'power'
          ? t('clRxAction.powerInvalid')
          : problem === 'cylinder'
            ? t('clRxAction.cylinderInvalid')
            : problem === 'add'
              ? t('clRxAction.addInvalid')
              : problem === 'mm'
                ? t('clRxAction.mmInvalid', {
                    bcMin: CONTACT_LENS_BASE_CURVE_MM.min.toFixed(2),
                    bcMax: CONTACT_LENS_BASE_CURVE_MM.max.toFixed(2),
                    diaMin: CONTACT_LENS_DIAMETER_MM.min.toFixed(1),
                    diaMax: CONTACT_LENS_DIAMETER_MM.max.toFixed(1),
                  })
                : problem.message

  return (
    <form
      aria-label={t('rxAction.heading', { version: rx.version })}
      onSubmit={(e) => {
        e.preventDefault()
        if (problem === null) void correct()
      }}
    >
      <h4>{t('rxAction.heading', { version: rx.version })}</h4>
      {(['od', 'os'] as const).map((side) => (
        <fieldset key={side}>
          <legend>
            {t(side === 'od' ? 'customerDetail.rx.row.right' : 'customerDetail.rx.row.left')}
          </legend>
          {text(side, 'power', t('clRx.col.power'))}
          {text(side, 'cylinder', t('rx.col.cylinder'))}
          {text(side, 'axis', t('rx.col.axis'))}
          {text(side, 'add', t('rx.col.add'))}
          {text(side, 'baseCurveMm', t('clRx.col.baseCurve'))}
          {text(side, 'diameterMm', t('clRx.col.diameter'))}
          {text(side, 'brand', t('clRx.col.brand'))}
          <label className="lm-field">
            <span className="lm-field__label">{t('clRx.col.modality')}</span>
            <select
              className="lm-input"
              value={forms[side].modality}
              onChange={(e) => {
                setField(side, 'modality', e.currentTarget.value)
              }}
            >
              {CONTACT_LENS_MODALITIES.map((m) => (
                <option key={m} value={m}>
                  {t(MODALITY_KEY[m])}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
      ))}
      <label className="lm-field">
        <span className="lm-field__label">{t('rxAction.reason')}</span>
        <input
          className="lm-input"
          type="text"
          required
          value={reason}
          onChange={(e) => {
            setReason(e.currentTarget.value)
          }}
        />
      </label>
      {message === null ? null : (
        <p className="lm-error" role="alert">
          {message}
        </p>
      )}
      <div className="lm-screen__actions">
        <button type="submit" className="lm-button lm-button--primary" disabled={problem !== null}>
          {t('rxAction.submit')}
        </button>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setOpen(false)
          }}
        >
          {t('rxAction.cancel')}
        </button>
      </div>
      <p className="lm-queue-state" role="status">
        {statusLine(t, snapshot, writeId, t('queue.subject.correction'))}
      </p>
    </form>
  )
}
